import { createSlice, freeze, PayloadAction } from '@reduxjs/toolkit';
import { IConversationThreadFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { IConversationThreadsState } from './types';
import isEqual from 'lodash/isEqual';

const initialState = freeze<IConversationThreadsState>(
  {
    threads: [],
    activeThread: null,
    isCommentMode: false,
    isCommentingDisabled: false,
  },
  true,
);

export default createSlice({
  name: 'conversationThreads',
  initialState,
  reducers: {
    populateThreads: (
      draft,
      action: PayloadAction<IConversationThreadFieldsFragment[]>,
    ) => {
      draft.threads = action.payload;
    },
    addThread: (
      draft,
      action: PayloadAction<IConversationThreadFieldsFragment>,
    ) => {
      draft.threads.unshift(action.payload);
    },
    updateThread: (
      draft,
      action: PayloadAction<IConversationThreadFieldsFragment>,
    ) => {
      const updatedThread = action.payload;
      assertCondition(draft.threads.length > 0);
      assertNotNil(draft.activeThread);

      const threadIndex = draft.threads.findIndex(
        (thread) => thread.id === updatedThread.id,
      );

      assertCondition(threadIndex !== -1);
      draft.threads[threadIndex] = updatedThread;
      draft.activeThread = updatedThread;
    },
    resolveThread: (
      draft,
      action: PayloadAction<IConversationThreadFieldsFragment>,
    ) => {
      const thread = action.payload;
      assertCondition(draft.threads.length > 0);
      assertNotNil(draft.activeThread);

      const filteredThreads = draft.threads.filter(
        (draftThread) => draftThread.id !== thread.id,
      );
      draft.threads = filteredThreads;

      // Change or reset active thread
      const entityThreads = filteredThreads.filter((draftThread) =>
        isEqual(draftThread.entity, thread.entity),
      );
      draft.activeThread = !entityThreads.length ? null : entityThreads[0];
    },
    setActiveThread: (
      draft,
      action: PayloadAction<IConversationThreadFieldsFragment | null>,
    ) => {
      draft.activeThread = action.payload;
    },
    toggleCommentMode: (draft) => {
      draft.isCommentMode = !draft.isCommentMode;
    },
    disableCommenting: (draft) => {
      draft.isCommentingDisabled = true;
    },
    resetConversationThreads: () => {
      return initialState;
    },
  },
});
